/** Bank statement values are calendar dates and wall-clock times, matching the
 * database's date / timestamp-without-time-zone columns. Never convert them to
 * UTC or infer a device timezone for a source string with explicit components.
 */
type CalendarParts = { year: number; month: number; day: number; tail: string };
const months: Record<string, number> = {
  jan: 1, januari: 1, january: 1, feb: 2, februari: 2, february: 2,
  mar: 3, maret: 3, march: 3, apr: 4, april: 4, mei: 5, may: 5,
  jun: 6, juni: 6, june: 6, jul: 7, juli: 7, july: 7, agu: 8, agt: 8,
  agustus: 8, aug: 8, august: 8, sep: 9, sept: 9, september: 9, okt: 10,
  oktober: 10, oct: 10, october: 10, nov: 11, november: 11, des: 12,
  desember: 12, dec: 12, december: 12,
};
const pad = (value: number, width = 2) => String(value).padStart(width, "0");
function validDate(year: number, month: number, day: number) {
  if (![year, month, day].every(Number.isInteger) || year < 1 || year > 9999 || month < 1 || month > 12) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return day >= 1 && day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}
function parts(value: unknown): CalendarParts | null {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate(), tail: "" };
  }
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  let match = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?=[T\s]|$)/);
  if (match) return { year: +match[1], month: +match[2], day: +match[3], tail: raw.slice(match[0].length) };
  match = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})(?=[T\s]|$)/);
  if (match) return { year: +match[3] < 100 ? +match[3] + 2000 : +match[3], month: +match[2], day: +match[1], tail: raw.slice(match[0].length) };
  match = raw.match(/^(\d{1,2})[-\s/]([A-Za-z]{3,9})[-\s/](\d{2,4})(?=[T\s]|$)/);
  if (match) return { year: +match[3] < 100 ? +match[3] + 2000 : +match[3], month: months[match[2].toLowerCase()] || 0, day: +match[1], tail: raw.slice(match[0].length) };
  // Retain unambiguous named-month English exports, including weekday prefixes.
  match = raw.match(/^(?:[A-Za-z]{3,9},?\s+)?([A-Za-z]{3,9})[-\s/](\d{1,2}),?[-\s/](\d{2,4})(?=[T\s]|$)/);
  if (match) return { year: +match[3] < 100 ? +match[3] + 2000 : +match[3], month: months[match[1].toLowerCase()] || 0, day: +match[2], tail: raw.slice(match[0].length) };
  return null;
}
export function parseStatementDate(value: unknown) {
  const p = parts(value);
  return p && validDate(p.year, p.month, p.day) ? `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}` : "";
}
export function parseStatementDateTime(value: unknown) {
  const date = parseStatementDate(value);
  if (!date) return "";
  if (value instanceof Date) {
    const millis = value.getMilliseconds();
    return `${date}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}${millis ? `.${pad(millis, 3)}` : ""}`;
  }
  const tail = parts(value)!.tail;
  const match = tail.match(/^[T\s]+(\d{1,2})[.:](\d{2})(?::(\d{2})([.,]\d{1,9})?)?\s*(AM|PM)?\s*(?:Z|[+-]\d{2}:?\d{2}|WIB|WITA|WIT|UTC|GMT(?:[+-]\d{2}:?\d{2})?)?\s*$/i);
  if (!match) return "";
  let hour = +match[1];
  const minute = +match[2], second = +(match[3] || 0), meridiem = match[5]?.toUpperCase();
  if (meridiem) { if (hour < 1 || hour > 12) return ""; hour = hour % 12 + (meridiem === "PM" ? 12 : 0); }
  if (hour > 23 || minute > 59 || second > 59) return "";
  return `${date}T${pad(hour)}:${pad(minute)}${match[3] ? `:${pad(second)}` : ""}${match[4] ? match[4].replace(",", ".") : ""}`;
}
